import { observable } from "@legendapp/state";
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { openAskAttendanceSituation } from '_pages/client/desk/components/AskSituation';
import { $npsOpen } from "_pages/client/desk/components/Nps";
import { open } from "_pages/client/desk/components/RequestIdentification";
import { Sound, SoundService } from "_services/sound.service";
import { HearingHub } from "_services/ws/hearing-hub";
import { NpsNote } from "_types/requests/nps-note";
import { ChatMessage, NewMessage } from "_types/responses/chat";
import { GeneratedDeskToken } from '_types/responses/generated-desk-token';
import { AttendanceStarted } from '_types/responses/Ws/desk/attendance';
import { QueueInfo } from "_types/responses/Ws/desk/queue-info";
import { DeskStartOnline } from "_types/responses/Ws/start-online";
import { enterVideoAttendance } from '_ui/Alerts';
import { iso2jsDate } from "_utils/date";
import { Route } from "_utils/url";
import { DateTime } from "luxon";
import Config from "../../config";
import { $clientEnded } from "_pages/client/desk/store";

const url = Config.API_URL + '/desk-hub';
const tokenStorageKey = (entityId: string) => `clientDesk_${entityId}`;

export const DeskHub = new class {
    private readonly _listenMap = new Map<string, (...args: any[]) => any>([
        ["Connected", this.onConnected.bind(this)],
        ["ReceiveMessage", this.onReceiveMessage.bind(this)],
        ["QueueInfo", this.QueueInfo.bind(this)],
        ["AttendanceStarted", this.AttendanceStarted.bind(this)],
        ["AttendanceFinished", this.AttendanceFinished.bind(this)],
        ["InvalidToken", this.InvalidToken.bind(this)],
        ["VideoStarted", this.VideoStarted.bind(this)],
    ]);

    private _hub: HubConnection | null = null;

    private _currentDeskId: string | null = null;

    private _$isConnected = observable(false);
    private _$token = observable<string | undefined>(undefined);

    public $myInfo = observable<QueueInfo | null>(null);

    public clientServedId = '';
    public onlyView = false;
    public currentAttendanceId = '';
    public $videoLink = observable<string | null>(null);

    public get $token() {
        return this._$token;
    }

    public get $isConnected() {
        return this._$isConnected;
    }

    public get currentDeskId() {
        return this._currentDeskId;
    }

    public set currentDeskId(value: string | null) {
        this._currentDeskId = value;
    }

    constructor() {
        this._$isConnected.onChange(() => {
            if (this.$token.get()) {
                this.startOnline();
            }
        });
    }

    public storeGeneratedToken(deskId: string, value: GeneratedDeskToken, forClient = false) {

        localStorage.setItem(
            tokenStorageKey(deskId),
            JSON.stringify(value)
        );

        this.clientServedId = value.clientServedId;

        this._$token.set(value.token);

        this.startOnline();
    }

    public loadGeneratedToken(deskId: string) {
        try {
            this.currentDeskId = deskId;
            const key = tokenStorageKey(deskId);
            const token = JSON.parse(localStorage.getItem(key) || '{}') as GeneratedDeskToken;

            if (token && token.token) {
                const expiration = iso2jsDate(token.expirationDate);
                if (!expiration || expiration < new Date()) {
                    localStorage.removeItem(key);
                    this._$token.set(undefined);
                    return false;
                }
                this._$token.set(token.token);
                this.clientServedId = token.clientServedId;

                this.startOnline();
                return true;
            }
            return false;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    public clearTokens() {
        if (this.currentDeskId) {
            localStorage.removeItem(tokenStorageKey(this.currentDeskId));
        }
        this._$token.set(undefined);
    }

    public async connect() {
        if (this._hub || this.onlyView) {
            return;
        }

        this._hub = new HubConnectionBuilder()
            .withUrl(url)
            .withAutomaticReconnect([0, 2000, 10000, 30000])
            .build();

        this._listenMap.forEach((handler, event) => {
            this._hub?.on(event, handler);

        });

        await this._hub.start();
    }

    public startOnline() {
        const startOnline = {
            deskId: this.currentDeskId,
            token: this._$token.get(),
        } as DeskStartOnline;

        if (!this.clientServedId) {
            return;
        }

        startOnline.clientServedId = this.clientServedId;

        this._hub?.invoke('Online', startOnline);
    }

    public npsNote(note: number) {
        this._hub?.invoke('Nps', {
            deskId: this._currentDeskId,
            token: this._$token.get(),
            note,
            clientServedId: this.clientServedId,
            attendanceId: this.currentAttendanceId,
        } as NpsNote);
    }

    public disconnect() {
        this.clearTokens();
        if (this._hub) {
            this._hub?.stop();
            this._hub = null;
            this._$isConnected.set(false);
        }
    }

    private onReceiveMessage(data: NewMessage) {
    }

    private AttendanceStarted(data: AttendanceStarted) {
        const { chatToken, attendanceId, attendant } = data;
        const { chatSessionId } = chatToken;

        SoundService.play(Sound.YourTime, true);
        this.currentAttendanceId = attendanceId;
        HearingHub.$messages.set([
            {
                content: `Iniciando atendimento, ${attendant} irá atender seu chamado.`,
                creationDate: DateTime.now().toSQLDate(),
            } as ChatMessage,
        ]);
        HearingHub.forClient();
        HearingHub.currentChatId = chatSessionId;

        HearingHub.storeGeneratedToken(
            chatSessionId,
            attendanceId,
            chatToken,
            false,
        );

        setTimeout(() => Route.go(window.location.pathname, 'atendimento', attendanceId), 100);
    }


    public async VideoStarted(link: string) {
        this.$videoLink.set(link);

        if ((await enterVideoAttendance()).isConfirmed) {
            window.open(link, '_BLANK');

        }
    }

    private AttendanceFinished() {
        if($clientEnded.get()) {
            return;
        }
        openAskAttendanceSituation();
    }

    private QueueInfo(queue: QueueInfo) {
        this.$myInfo.set(queue);
    }

    private InvalidToken() {
        if (this._currentDeskId) {
            localStorage.removeItem(tokenStorageKey(this._currentDeskId!));
        }
        open();
    }

    private onConnected() {
        this._$isConnected.set(true);
    }
};
